/* SPDX-License-Identifier: AGPL-3.0-or-later */

import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import {MatCard, MatCardModule} from '@angular/material/card';
import {
    QualificationTeamScoreDto,
    TournamentEndpointService,
    TournamentQualificationMatchDto,
} from '@api';
import {catchError, fromEvent, of, Subject, takeUntil} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TournamentScoreTableComponent} from '../../tournament-score-table/tournament-score-table.component';
import {QualificationMatchesComponent} from '../../qualification-matches/qualification-matches.component';

@Component({
    selector: 'app-tournament-public-view',
    standalone: true,
    imports: [
        MatCard,
        TournamentScoreTableComponent,
        QualificationMatchesComponent,
        MatCardModule,
    ],
    templateUrl: './tournament-public-view.component.html',
    styleUrl: './tournament-public-view.component.scss',
})
export class TournamentPublicViewComponent implements OnInit, OnDestroy, AfterViewInit {
    tournamentService = inject(TournamentEndpointService);
    snackBar = inject(MatSnackBar);

    tournamentId = input<number>();
    scoreDtos = signal<QualificationTeamScoreDto[]>([]);
    qualificationMatches = signal<TournamentQualificationMatchDto[]>([]);
    nextMatches = computed(() =>
        this.qualificationMatches().filter(m => m.table === null && m.endTime === null),
    );
    runningMatches = computed(() => this.qualificationMatches().filter(m => m.table !== null));
    playedMatches = computed(() =>
        this.qualificationMatches().filter(m => m.table === null && m.endTime !== null),
    );
    currentlyPlayingTeamIds = computed(() =>
        this.runningMatches().map(m => m.participants?.map(it => it.teamId))
            .filter(it => it !== undefined)
            .flat()
            .map(it => it!)
    )
    token = input<string>();

    scoreContainer = viewChild<ElementRef>('scoreContainer');

    currentScrollTop = 1;

    intervals: number[] = [];
    private unsubscribe$ = new Subject();

    scoreContainerClientHeight: number = 0;

    ngAfterViewInit(): void {
        this.scoreContainerClientHeight = this.scoreContainer()?.nativeElement.clientHeight;
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.scoreContainerClientHeight = this.scoreContainer()?.nativeElement.clientHeight;
            });
        this.startAutoScroll();
    }

    ngOnInit(): void {
        if (this.token()) {
            this.loadScores(this.token()!);
            this.loadQualificationMatches(this.token()!);
        }
        this.intervals.push(
            window.setInterval(() => {
                if (this.token()) {
                    this.loadQualificationMatches(this.token()!);
                }
            }, 3000),
        );
    }

    ngOnDestroy(): void {
        this.intervals.forEach(intervalId => {
            clearInterval(intervalId);
        });
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    loadScores(token: string): void {
        if (this.tournamentId() !== undefined) {
            this.tournamentService
                .getTournamentQualificationScoreTablePublic(this.tournamentId()!, token)
                .pipe(
                    catchError(error => {
                        console.error(error);
                        this.snackBar.open('Failed to load teams: ' + error.message, 'Close');
                        return of([]);
                    }),
                )
                .subscribe((scores: QualificationTeamScoreDto[]) => {
                    this.scoreDtos.set(scores);
                });
        }
    }

    loadQualificationMatches(token: string): void {
        if (this.tournamentId() !== undefined) {
            this.tournamentService
                .getQualificationMatchesPublic(this.tournamentId()!, token)
                .pipe(
                    catchError(error => {
                        console.error(error);
                        this.snackBar.open('Failed to load teams: ' + error.message, 'Close');
                        return of([]);
                    }),
                )
                .subscribe((matches: TournamentQualificationMatchDto[]) => {
                    this.qualificationMatches.set(matches);
                });
        }
    }

    startAutoScroll(): void {
        const scrollContainer = this.scoreContainer()!.nativeElement;
        this.intervals.push(
            window.setInterval(() => {
                const scrollStep = this.scoreContainerClientHeight * 0.5;
                const lastScrollTop = this.currentScrollTop;
                if (this.currentScrollTop > scrollContainer.scrollHeight - this.scoreContainerClientHeight) {
                    this.currentScrollTop = 1;
                } else {
                    this.currentScrollTop += scrollStep;
                }
                scrollContainer.scrollTo({top: lastScrollTop, behavior: 'instant'});
                scrollContainer.scrollTo({top: this.currentScrollTop, behavior: 'smooth'});
                this.loadScores(this.token()!);
            }, 5000),
        );
    }
}
